import { useMemo, useState } from 'react';
import { BrainIcon, CheckCircleIcon, PencilSimple } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import {
  useAcceptProposalWithEdit,
  useBatchProposalAction,
  useReflectProposalsAll,
  useRejectProposals,
} from '~/hooks/queries/use-reflect';
import {
  draftFromProposal,
  editFailureCopy,
  editFromReflectionDraft,
  reflectionDraftBlocker,
  type ReflectionDraft,
} from '~/features/memory/reflection-edit-draft';
import { ReflectionEditFields } from '~/features/memory/reflection-edit-form';
import type { OnboardingRecapItem } from './tool-signals';
import {
  buildRecapRows,
  deriveRecapDecision,
  recapHeader,
  rowBadge,
  type RecapDecision,
  type RecapRow,
} from './onboarding-recap-rows';

export { deriveRecapDecision } from './onboarding-recap-rows';

const YES = 'h-8 active:scale-[0.96] text-[length:var(--text-sm)] bg-success/15 text-success hover:bg-success/25';
const NO = 'h-8 active:scale-[0.96] text-[length:var(--text-sm)] bg-destructive/15 text-destructive hover:bg-destructive/25';
const THIRD = 'relative h-8 active:scale-[0.96] after:absolute after:-inset-1 after:content-[\'\'] shrink-0 text-[length:var(--text-sm)] text-muted-foreground hover:text-foreground';

/** Which row's Edit is open, the proposal it edits, and its draft. */
export interface RecapEditing {
  index: number;
  proposalId: number;
  draft: ReflectionDraft;
}

function RecapRowEdit({
  row,
  draft,
  onChange,
  busy,
  failure,
  onSave,
  onLeaveOut,
  onCancel,
}: {
  row: RecapRow;
  draft: ReflectionDraft;
  onChange: (next: ReflectionDraft) => void;
  busy: boolean;
  failure: string | null;
  onSave: () => void;
  onLeaveOut: () => void;
  onCancel: () => void;
}) {
  const blocker = reflectionDraftBlocker(draft);
  return (
    <div
      data-testid="recap-row-edit"
      className="space-y-2 rounded-[14px] border border-amber-500/30 bg-amber-500/5 p-2"
    >
      <ReflectionEditFields draft={draft} onChange={onChange} />
      {blocker && <p className="text-[length:var(--text-sm)] text-destructive">{blocker}</p>}
      {failure && <p className="text-[length:var(--text-sm)] text-destructive">{failure}</p>}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" disabled={busy || blocker !== null} className={YES} onClick={onSave}>
          {busy ? 'Saving…' : 'Save this version'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} className={NO} onClick={onLeaveOut}>
          Leave this out
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} className="h-8 active:scale-[0.96] text-[length:var(--text-sm)]" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <span className="sr-only">Editing {row.label}</span>
    </div>
  );
}

/**
 * FRONT-907 (2026-07-21 review): the day-one interview's closing recap —
 * without this, the interview captured facts but nothing ever reached
 * memory. This card shows what `capture_perfect_day_answers` staged and lets
 * the user approve or skip it in the SAME conversation, reusing the reflect
 * batch-accept/reject routes scoped to this run's id.
 *
 * FRONT-1014 (Marko, live-test): the decision is read from the run's actual
 * proposal statuses so a remount reflects reality; `localDecided` is a
 * same-mount optimistic overlay so the footer click flips instantly.
 *
 * FRONT-2349 (D5): each row has its own Edit — the phone's pattern — which
 * turns that row into its field with Save this version · Leave this out ·
 * Cancel. A row settles on its own (accepting or rejecting just its
 * proposal); the footer's Save the rest / Skip then covers only what is
 * still pending, and is disabled while a row is open. Key people is decided
 * as a group and has no Edit (see `onboarding-recap-rows.ts`).
 */
export function OnboardingRecapCard({
  runId,
  items,
  initialEditing = null,
}: {
  runId: number;
  items: OnboardingRecapItem[];
  /** Test seam: the static-markup suite has no clicks, so it opens a row by seeding it. */
  initialEditing?: RecapEditing | null;
}) {
  const batchAction = useBatchProposalAction();
  const acceptEdited = useAcceptProposalWithEdit();
  const rejectRows = useRejectProposals();
  const [localDecided, setLocalDecided] = useState<RecapDecision>(null);
  const [editing, setEditing] = useState<RecapEditing | null>(initialEditing);
  const [failure, setFailure] = useState<string | null>(null);
  const { data: allProposals } = useReflectProposalsAll();
  const rows = useMemo(() => buildRecapRows(items ?? [], allProposals, runId), [items, allProposals, runId]);
  const serverDecided = useMemo(() => deriveRecapDecision(allProposals, runId), [allProposals, runId]);
  const decided = localDecided ?? serverDecided;

  if (!items?.length) return null;

  const rowBusy = acceptEdited.isPending || rejectRows.isPending;
  const busy = batchAction.isPending || rowBusy;
  const anySettled = rows.some((r) => r.status !== 'pending');

  const closeEdit = () => {
    setEditing(null);
    setFailure(null);
  };

  const saveRow = () => {
    if (!editing) return;
    setFailure(null);
    // Always sent, even if a refetch already shows the row decided: the
    // server's answer is what tells the person it didn't go through.
    acceptEdited.mutate(
      { id: editing.proposalId, edit: editFromReflectionDraft(editing.draft) },
      { onSuccess: closeEdit, onError: (err) => setFailure(editFailureCopy(err)) },
    );
  };

  const leaveOut = (row: RecapRow) => {
    rejectRows.mutate(row.proposalIds, { onSuccess: closeEdit });
  };

  return (
    <div className="border border-border rounded-lg bg-card max-w-[85%] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <BrainIcon size={18} className="shrink-0 text-muted-foreground" />
        <span className="text-base font-medium text-foreground">{recapHeader(decided, rows)}</span>
        {(decided === 'accepted' || decided === 'partial') && (
          <CheckCircleIcon size={18} className="shrink-0 text-success ml-auto" />
        )}
      </div>

      <ul className="px-4 py-3 space-y-2">
        {rows.map((row, index) => {
          const open = editing?.index === index;
          const badge = rowBadge(row, rows, decided !== null);
          const actionable = decided === null && row.status === 'pending' && editing === null;
          return (
            <li key={`${row.label}-${index}`} data-testid="recap-row" className="text-base">
              {open ? (
                <RecapRowEdit
                  row={row}
                  draft={editing.draft}
                  onChange={(draft) => setEditing({ ...editing, draft })}
                  busy={rowBusy}
                  failure={failure}
                  onSave={saveRow}
                  onLeaveOut={() => leaveOut(row)}
                  onCancel={closeEdit}
                />
              ) : (
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="font-semibold text-foreground">{row.label}: </span>
                    <span className="text-muted-foreground text-pretty">{row.value}</span>
                  </div>
                  {actionable && row.editable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      className={THIRD}
                      onClick={() => {
                        const draft = draftFromProposal(row.editable!);
                        if (draft) setEditing({ index, proposalId: row.editable!.id, draft });
                      }}
                    >
                      <PencilSimple size={14} weight="bold" /> Edit
                    </Button>
                  )}
                  {actionable && row.group && row.proposalIds.length > 0 && (
                    <Button size="sm" variant="ghost" disabled={busy} className={THIRD} onClick={() => leaveOut(row)}>
                      Leave out
                    </Button>
                  )}
                  {badge && (
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[length:var(--text-xs)] font-medium text-muted-foreground">
                      {badge}
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {decided === null ? (
        <div className="flex flex-col gap-2.5 px-4 py-3 border-t border-border">
          <span className="text-sm text-muted-foreground text-pretty">
            {anySettled
              ? 'The rest goes into memory when you save. Nothing else is saved until then.'
              : 'This is what goes into memory — nothing saves until you approve.'}
          </span>
          {/* FRONT-2349 (D7): yes-first — Save · Skip. Both act only on what
              is still pending, and wait while a row is being edited. */}
          <div data-testid="recap-footer" className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={busy || editing !== null}
              className={YES}
              onClick={() =>
                batchAction.mutate(
                  { action: 'accept', runId },
                  {
                    onSuccess: () => {
                      const anyLeftOut = rows.some((r) => r.status === 'left-out');
                      setLocalDecided(anyLeftOut ? 'partial' : 'accepted');
                    },
                  },
                )
              }
            >
              {batchAction.isPending ? 'Saving…' : anySettled ? 'Save the rest' : 'Save to memory'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy || editing !== null}
              className={NO}
              onClick={() =>
                batchAction.mutate(
                  { action: 'reject', runId },
                  {
                    onSuccess: () => {
                      const anySaved = rows.some((r) => r.status === 'saved' || r.status === 'edited');
                      setLocalDecided(anySaved ? 'partial' : 'declined');
                    },
                  },
                )
              }
            >
              Skip
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-4 py-3 border-t border-border">
          <span className="text-sm text-muted-foreground">
            {decided === 'declined'
              ? 'You can always tell EDDY this again later.'
              : 'Live on your Memory page now.'}
          </span>
        </div>
      )}
    </div>
  );
}
